const formatToLowerCaseData = (obj) =>
  Object.fromEntries(Object.entries(obj).map(([k, v]) => [k.toLowerCase(), v]));

export const generatePcdf = async ({
  modelYear,
  modelCode,
  userId,
  requestTime,
  userName,
  imdVersion,
  priceMatrixVersion,
  pplVersion,
  startDate,
  endDate,
  dataSetId,
}) => {
  try {
    const url = `${process.env.REACT_APP_GENERATE_PCDF_ENDPOINT}?modelYear=${modelYear}&modelCode=${modelCode}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        userid: userId,
        requesttime: requestTime,
        username: userName,
        imdversion: imdVersion,
        pricematrixversion: priceMatrixVersion,
        pplversion: pplVersion,
        startdate: startDate,
        enddate: endDate,
        datasetid: dataSetId,
      },
    });

    if (response.status === 501) {
      const errorMsg = await response.text();
      throw new Error(errorMsg);
    }
    if (response.status === 503) {
      throw new Error('Service unavailable');
    }
    return { data: await response.text() };
  } catch (error) {
    return { error: error.message };
  }
};

export const generateTaxImportFile = async ({
  userId,
  requestTime,
  userName,
  imdVersion,
}) => {
  try {
    const response = await fetch(
      process.env.REACT_APP_GENERATE_TAX_IMPORT_ENDPOINT,
      {
        method: 'POST',
        headers: {
          userid: userId,
          requesttime: requestTime,
          username: userName,
          imdversion: imdVersion,
        },
      },
    );

    if (response.status === 501) {
      const errorMsg = await response.text();
      throw new Error(errorMsg);
    }
    if (response.status === 503) {
      throw new Error('Service unavailable');
    }
    return { data: await response.text() };
  } catch (error) {
    return { error: error.message };
  }
};

export const generateMvdf = async ({
  modelYear,
  modelCode,
  userId,
  requestTime,
  userName,
  pplVersion,
  priceMatrixVersion,
  nomenclatureGuideVersion,
  techSpecsVersion,
}) => {
  try {
    const url = `${process.env.REACT_APP_GENERATE_MVDF_ENDPOINT}?modelYear=${modelYear}&modelCode=${modelCode}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        userid: userId,
        requesttime: requestTime,
        username: userName,
        pplversion: pplVersion,
        pricematrixversion: priceMatrixVersion,
        nomenclatureguideversion: nomenclatureGuideVersion,
        techspecsversion: techSpecsVersion,
      },
    });

    if (response.status === 501) {
      const errorMsg = await response.text();
      throw new Error(errorMsg);
    }
    if (response.status === 503) {
      throw new Error('Service unavailable');
    }
    return { data: await response.text() };
  } catch (error) {
    return { error: error.message };
  }
};

export const generatePriceGuide = async ({
  modelYear,
  modelCode,
  userId,
  requestTime,
  userName,
  pplVersion,
  priceMatrixVersion,
  nomenclatureGuideVersion,
  techSpecsVersion,
  effectiveDate,
}) => {
  try {
    const url = `${process.env.REACT_APP_GENERATE_PRICE_GUIDE_ENDPOINT}?modelYear=${modelYear}&modelCode=${modelCode}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        userid: userId,
        requestime: requestTime,
        username: userName,
        pplversion: pplVersion,
        pricematrixversion: priceMatrixVersion,
        nomenclatureguideversion: nomenclatureGuideVersion,
        techspecsversion: techSpecsVersion,
        effectivedate: effectiveDate,
      },
    });

    if (response.status === 501) {
      const errorMsg = await response.text();
      throw new Error(errorMsg);
    }
    if (response.status === 503) {
      throw new Error('Service unavailable');
    }
    return { data: await response.text() };
  } catch (error) {
    return { error: error.message };
  }
};

export const generateToolbox = async ({
  userId,
  requestTime,
  userName,
  imdVersion,
  modelYear,
  pplModelCodes,
  pplVersions,
  effectiveStartDate,
  effectiveEndDate,
}) => {
  try {
    const url = `${process.env.REACT_APP_GENERATE_TOOLBOX_ENDPOINT}?modelYear=${modelYear}`;
    const modelCodeQueryParams = pplModelCodes.map(
      (code) => `modelCode=${code}`,
    );

    const pplVersionsFormatted = formatToLowerCaseData(pplVersions);

    const response = await fetch(`${url}&${modelCodeQueryParams.join('&')}`, {
      method: 'POST',
      headers: {
        userid: userId,
        requesttime: requestTime,
        username: userName,
        imdversion: imdVersion,
        effectivestartdate: effectiveStartDate,
        effectiveenddate: effectiveEndDate,
        ...pplVersionsFormatted,
      },
    });

    if (response.status === 501) {
      const errorMsg = await response.text();
      throw new Error(errorMsg);
    }
    if (response.status === 503) {
      throw new Error('Service unavailable');
    }
    return { data: await response.text() };
  } catch (error) {
    return { error: error.message };
  }
};

export const generatePfa = async ({
  userId,
  requestTime,
  userName,
  imdVersion,
  modelYear,
  pplModelCodes,
  pplVersions,
  effectiveStartDate,
  effectiveEndDate,
}) => {
  try {
    const url = `${process.env.REACT_APP_GENERATE_PFA_ENDPOINT}?modelYear=${modelYear}`;
    const modelCodeQueryParams = pplModelCodes.map(
      (code) => `modelCode=${code}`,
    );

    const pplVersionsFormatted = formatToLowerCaseData(pplVersions);

    const response = await fetch(`${url}&${modelCodeQueryParams.join('&')}`, {
      method: 'POST',
      headers: {
        userid: userId,
        requesttime: requestTime,
        username: userName,
        imdversion: imdVersion,
        effectivestartdate: effectiveStartDate,
        effectiveenddate: effectiveEndDate,
        ...pplVersionsFormatted,
      },
    });

    if (response.status === 501) {
      const errorMsg = await response.text();
      throw new Error(errorMsg);
    }
    if (response.status === 503) {
      throw new Error('Service unavailable');
    }
    return { data: await response.text() };
  } catch (error) {
    return { error: error.message };
  }
};

export const generateVehicleTechSpecs = async ({
  modelYear,
  modelCode,
  userId,
  requestTime,
  userName,
  pplVersion,
  techspecVersion,
}) => {
  try {
    const url = `${process.env.REACT_APP_GENERATE_VEHICLE_TECH_SPECS_ENDPOINT}?modelYear=${modelYear}&modelCode=${modelCode}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        userid: userId,
        requesttime: requestTime,
        username: userName,
        pplversion: pplVersion,
        modelyear: modelYear,
        modelcode: modelCode,
        techspecversion: techspecVersion,
      },
    });

    if (response.status === 501) {
      const errorMsg = await response.text();
      throw new Error(errorMsg);
    }
    return { data: await response.text() };
  } catch (error) {
    return { error: error.message };
  }
};

export const generateTopFeatures = async ({
  modelYear,
  modelCode,
  userId,
  requestTime,
  userName,
  pplVersion,
  nomenclatureGuideVersion,
}) => {
  try {
    const url = `${process.env.REACT_APP_GENERATE_TOP_FEATURES_ENDPOINT}?modelYear=${modelYear}&modelCode=${modelCode}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        userid: userId,
        requesttime: requestTime,
        username: userName,
        pplversion: pplVersion,
        nomenclatureguideversion: nomenclatureGuideVersion,
      },
    });

    if (response.status === 501) {
      const errorMsg = await response.text();
      throw new Error(errorMsg);
    }
    if (response.status === 503) {
      throw new Error('Service unavailable');
    }
    return { data: await response.text() };
  } catch (error) {
    return { error: error.message };
  }
};

export const generateFeaturesGuide = async ({
  modelYear,
  modelCode,
  userId,
  requestTime,
  userName,
  pplVersion,
  nomenclatureGuideVersion,
  effectiveDate,
}) => {
  try {
    const url = `${process.env.REACT_APP_GENERATE_FEATURES_GUIDE_ENDPOINT}?modelYear=${modelYear}&modelCode=${modelCode}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        userid: userId,
        requesttime: requestTime,
        username: userName,
        pplversion: pplVersion,
        nomenclatureguideversion: nomenclatureGuideVersion,
        effectivedate: effectiveDate,
      },
    });

    if (response.status === 501) {
      const errorMsg = await response.text();
      throw new Error(errorMsg);
    }
    if (response.status === 503) {
      throw new Error('Service unavailable');
    }
    return { data: await response.text() };
  } catch (error) {
    return { error: error.message };
  }
};
